import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Chip, Paper, Tabs, Tab,
  useMediaQuery, LinearProgress, Grid, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  PlayArrow as PlayIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../utils/api';
import TrainingPlayer from '../components/TrainingPlayer';
import { useAuth } from '../contexts/AuthContext';
import { calendarDaysUntil, formatDateOnly, formatStoredDateTime } from '../utils/dateFormat';

interface Assignment {
  id: number;
  assignment_type: string;
  content_title: string;
  description: string;
  status: string;
  due_date: string;
  is_overdue: boolean;
  completion_count: number;
  is_completed: boolean;
  assigned_by: {
    email: string;
    first_name: string;
    last_name: string;
  };
  assignment_completions: Array<{
    id: number;
    status: string;
    started_at: string;
    completed_at: string;
    score: number;
    attempts: number;
    assignee?: {
      id: number;
    };
  }>;
  object_id: string; // Add this to get the training ID
  document_acknowledgment?: {
    id: number;
    status: string;
    acknowledged_at: string;
    created_at: string;
  } | null;
  revision_info?: {
    version: string;
    is_current_version: boolean;
    revision_date: string;
    has_been_revised: boolean;
    is_outdated: boolean;
    current_version?: {
      version: string;
      revision_date: string;
      id: number;
    };
  } | null;
}

interface Training {
  id: number;
  title: string;
  description: string;
  training_type: string;
  sections: any[];
}

const MyAssignments: React.FC = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(1); // Default to Pending tab
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null); // Add training state
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Debug: Track all state changes
  React.useEffect(() => {
    console.log('🎮 isPlaying changed to:', isPlaying);
  }, [isPlaying]);
  
  React.useEffect(() => {
    console.log('📚 selectedTraining changed:', selectedTraining?.id);
  }, [selectedTraining]);
  
  React.useEffect(() => {
    console.log('⏳ loading changed to:', loading);
  }, [loading]);
  const [completionDialog, setCompletionDialog] = useState(false);
  const [completionResults, setCompletionResults] = useState<any>(null);
  const [assignmentTypeFilter, setAssignmentTypeFilter] = useState<string>('all'); // Filter by assignment type

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyAssignments();
  }, []);

  // Refresh assignments when page becomes visible (user navigates back)
  // BUT NOT when the training player is active (to prevent unmounting)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isPlaying) {
        fetchMyAssignments();
      }
    };

    const handleFocus = () => {
      // Don't refresh while training is playing to prevent unmounting the player
      if (!isPlaying) {
        fetchMyAssignments();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isPlaying]); // Add isPlaying as dependency

  const fetchMyAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use the dedicated my_assignments endpoint
      const response = await api.get('/assignments/my_assignments/');
      const list = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setAssignments(list as any);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssignment = async (assignment: Assignment) => {
    // Clear any previous errors
    setError(null);
    
    // Check if this is a checklist assignment
    if (assignment.assignment_type === 'checklist') {
      // Navigate to checklist player
      navigate(`/checklist-player/${assignment.id}`);
      return;
    }

    if (assignment.assignment_type === 'log') {
      navigate(`/log-player/${assignment.id}`);
      return;
    }

    // Check if this is a document assignment
    if (assignment.assignment_type === 'document') {
      console.log('Navigating to document review:', `/document-review/${assignment.object_id}?assignment=${assignment.id}`);
      navigate(`/document-review/${assignment.object_id}?assignment=${assignment.id}`);
      return;
    }

    // For training assignments, fetch fresh assignment data to get latest progress
    if (assignment.assignment_type === 'training') {
      try {
        setLoading(true);
        
        // Fetch fresh assignment data with completion info
        const assignmentResponse = await api.get(`/assignments/${assignment.id}/`);
        const freshAssignment = assignmentResponse.data;
        
        // Check if it's already completed
        const myCompletion = freshAssignment.assignment_completions?.find((comp: any) => comp.status === 'completed');
        if (myCompletion) {
          // Training is completed, allow review
          await handleReviewCompletedTraining(freshAssignment);
          setLoading(false);
          return;
        }
        
        // Fetch training data
        const response = await api.get(`/trainings/${assignment.object_id}/`);
        console.log('Training data fetched:', response.data);
        console.log('Training sections:', response.data?.sections);
        console.log('First section content blocks:', response.data?.sections?.[0]?.content_blocks);
        
        // Validate training data structure
        if (!response.data?.sections || !Array.isArray(response.data.sections) || response.data.sections.length === 0) {
          throw new Error('Training data is incomplete or missing sections');
        }
        
        // Batch state updates to prevent multiple re-renders
        // Set isPlaying FIRST, then set the training data
        // This way, when the training data is set, the component renders with isPlaying=true
        setIsPlaying(true);
        setSelectedAssignment(freshAssignment); // Use fresh assignment data
        setSelectedTraining(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading training:', error);
        setError('Failed to load training. Please try again.');
        setLoading(false);
      }
    }
  };

  const handleReviewCompletedChecklist = async (assignment: Assignment) => {
    // Clear any previous errors
    setError(null);
    
    // Navigate to checklist player in read-only mode
    navigate(`/checklist-player/${assignment.id}?readonly=true`);
  };

  const handleReviewCompletedTraining = async (assignment: Assignment) => {
    // Clear any previous errors
    setError(null);
    
    try {
      setLoading(true);
      const response = await api.get(`/trainings/${assignment.object_id}/`);
      console.log('Training data fetched for review:', response.data);
      
      // Validate training data structure
      if (!response.data?.sections || !Array.isArray(response.data.sections) || response.data.sections.length === 0) {
        throw new Error('Training data is incomplete or missing sections');
      }
      
      // Get completion data for this assignment
      const myCompletion = assignment.assignment_completions?.find(comp => comp.status === 'completed');
      console.log('Completion data for review:', myCompletion);
      
      setSelectedTraining(response.data);
      setSelectedAssignment(assignment);
      setIsPlaying(true);
    } catch (error) {
      console.error('Error loading training for review:', error);
      setError('Failed to load training for review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrainingComplete = async (results: any) => {
    console.log('Training completed:', results);
    setCompletionResults(results);
    setCompletionDialog(true);
    setIsPlaying(false);
    
    // Save completion data to backend
    console.log('selectedAssignment:', selectedAssignment);
    console.log('selectedTraining:', selectedTraining);
    
    if (selectedAssignment) {
      const currentUserId = user?.id;
      if (!currentUserId) {
        console.error('Cannot save training completion: user is not authenticated');
        return;
      }

      // Prepare completion data outside try block so it's accessible in catch
      const completionData = {
        assignment: selectedAssignment.id,
        assignee_id: currentUserId,
        status: 'completed',
        completed_at: results.completedAt.toISOString(),
        score: results.testScore || null, // Only use test score for overall training score
        attempts: 1,
        notes: 'Training completed successfully',
        completion_data: {
          sections_completed: selectedTraining?.sections?.length || 1,
          total_sections: selectedTraining?.sections?.length || 1,
          duration_minutes: results.duration,
          quiz_score: results.quizScore || null, // Quiz scores for reporting only
          test_score: results.testScore || null, // Primary scoring mechanism
          test_passed: results.testPassed || null,
          quiz_results: results.quizResults,
          test_results: results.testResults,
          completed_at: results.completedAt.toISOString()
        }
      };
      
      try {
        
        console.log('Saving training completion:', completionData);
        
        // Check if completion record already exists
        console.log('Checking for existing completions...');
        const existingCompletions = await api.get(`/assignment-completions/?assignment=${selectedAssignment.id}`);
        console.log('Existing completions response:', existingCompletions.data);
        
        const userCompletion = existingCompletions.data.results?.find((comp: any) => {
          const assigneeId = comp.assignee_id || comp.assignee?.id || comp.assignee;
          return assigneeId === currentUserId;
        });
        console.log('Found existing completion:', userCompletion);
        
        if (userCompletion) {
          // Update existing completion
          console.log(`Updating existing completion ${userCompletion.id}`);
          await api.patch(`/assignment-completions/${userCompletion.id}/`, completionData);
          console.log('Successfully updated completion');
        } else {
          // Create new completion
          console.log('Creating new completion');
          await api.post('/assignment-completions/', completionData);
          console.log('Successfully created completion');
        }
      } catch (error: any) {
        console.error('Error saving training completion:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        if (error.response?.data?.non_field_errors) {
          console.error('Non-field errors:', error.response.data.non_field_errors);
        }
        
        // If we get a unique constraint error, try to find and update the existing record
        if (error.response?.status === 400 && error.response?.data?.non_field_errors) {
          try {
            console.log('Attempting to recover from unique constraint error...');
            // Query all completions for this assignment again
            const allCompletions = await api.get(`/assignment-completions/`);
            const existingCompletion = allCompletions.data.results?.find((comp: any) => 
              comp.assignment === selectedAssignment.id && 
              (comp.assignee?.id === currentUserId || comp.assignee_id === currentUserId || comp.assignee === currentUserId)
            );
            
            if (existingCompletion) {
              console.log('Found existing completion, updating:', existingCompletion);
              await api.patch(`/assignment-completions/${existingCompletion.id}/`, completionData);
              console.log('Successfully updated completion after recovery');
            } else {
              console.error('Could not find existing completion to update');
            }
          } catch (recoveryError) {
            console.error('Recovery attempt failed:', recoveryError);
          }
        }
      }
    } else {
      console.warn('No selectedAssignment found, cannot save completion');
    }
    
    // Note: fetchMyAssignments() removed to prevent double modal display
    // The assignments will be refreshed when the user closes the completion dialog
  };

  const handleCloseTraining = () => {
    setIsPlaying(false);
    setSelectedAssignment(null);
    setSelectedTraining(null); // Clear training data as well
  };

  const handleCloseCompletionDialog = () => {
    setCompletionDialog(false);
    setCompletionResults(null);
    // Refresh assignments to show updated status
    fetchMyAssignments();
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'pending': 'warning',
      'in_progress': 'info',
      'completed': 'success',
      'overdue': 'error',
      'cancelled': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string, isOverdue: boolean) => {
    if (isOverdue) return <WarningIcon />;
    switch (status) {
      case 'completed': return <CheckIcon />;
      case 'in_progress': return <ScheduleIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No due date';
    return formatDateOnly(dateString, 'No due date');
  };

  const getDaysUntilDue = (dueDate: string) => calendarDaysUntil(dueDate);

  const isCompliant = (assignment: Assignment) => {
    if (assignment.status === 'completed' || assignment.is_completed) {
      return true;
    }
    if (assignment.document_acknowledgment?.status === 'acknowledged') {
      return true;
    }
    return (assignment.assignment_completions || []).some(
      (completion) => completion.status === 'completed' && completion.assignee?.id === user?.id
    );
  };

  const getAssignmentTypeColor = (assignmentType: string) => {
    switch (assignmentType.toLowerCase()) {
      case 'checklist':
        return 'error'; // Red
      case 'training':
        return 'primary'; // Blue
      case 'document':
        return 'success'; // Green
      case 'log':
        return 'warning'; // Orange
      default:
        return 'default';
    }
  };

  // Helper functions for filtering assignments
  const getPendingAssignments = () => {
    return (assignments || []).filter(a => {
      // Apply assignment type filter
      if (assignmentTypeFilter !== 'all' && a.assignment_type !== assignmentTypeFilter) {
        return false;
      }
      
      if (a.assignment_type === 'document') {
        // For documents, check if acknowledged
        return !a.document_acknowledgment || a.document_acknowledgment.status !== 'acknowledged';
      }
      return a.status === 'pending' || a.is_overdue;
    });
  };

  const getInProgressAssignments = () => {
    return (assignments || []).filter(a => {
      // Apply assignment type filter
      if (assignmentTypeFilter !== 'all' && a.assignment_type !== assignmentTypeFilter) {
        return false;
      }
      
      if (a.assignment_type === 'document') {
        // Documents don't have "in progress" state
        return false;
      }
      return a.status === 'in_progress';
    });
  };

  const getCompletedAssignments = () => {
    return (assignments || []).filter(a => {
      // Apply assignment type filter
      if (assignmentTypeFilter !== 'all' && a.assignment_type !== assignmentTypeFilter) {
        return false;
      }
      
      if (a.assignment_type === 'document') {
        // For documents, check if acknowledged
        return a.document_acknowledgment && a.document_acknowledgment.status === 'acknowledged';
      }
      return a.status === 'completed';
    });
  };

  const getFilteredAssignments = () => {
    switch (selectedTab) {
      case 0: // All
        return (assignments || []).filter(a => 
          assignmentTypeFilter === 'all' || a.assignment_type === assignmentTypeFilter
        );
      case 1: // Pending (includes overdue)
        return getPendingAssignments();
      case 2: // In Progress
        return getInProgressAssignments();
      case 3: // Completed
        return getCompletedAssignments();
      default:
        return assignments || [];
    }
  };

  const filteredAssignments = getFilteredAssignments();

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Typography variant="h4" gutterBottom>
          My Assignments
        </Typography>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading assignments...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Typography variant="h4" gutterBottom>
          My Assignments
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchMyAssignments}>
          Retry
        </Button>
      </Box>
    );
  }

  if (isPlaying && selectedAssignment && selectedTraining) {
    console.log('🎬 MyAssignments rendering training area - Training ID:', selectedTraining.id, 'isPlaying:', isPlaying);
    
    // Additional validation to ensure training data is complete
    if (!selectedTraining.sections || !Array.isArray(selectedTraining.sections) || selectedTraining.sections.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Training data is incomplete. Please try again.
          </Alert>
          <Button variant="contained" onClick={handleCloseTraining}>
            Close
          </Button>
        </Box>
      );
    }
    
    const assignmentCompletion = selectedAssignment?.assignment_completions?.find((comp: any) => {
      const assigneeId = comp.assignee?.id ?? comp.assignee_id ?? comp.assignee;
      return Number(assigneeId) === Number(user?.id);
    });
    
    return (
      <TrainingPlayer
        key={`training-${selectedTraining.id}`}
        training={selectedTraining}
        assignmentId={selectedAssignment?.id}
        assignmentCompletion={assignmentCompletion}
        onComplete={handleTrainingComplete}
        onClose={handleCloseTraining}
        isReviewMode={assignmentCompletion?.status === 'completed'}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h4" gutterBottom>
        My Assignments
      </Typography>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {getPendingAssignments().length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="info.main">
                {getInProgressAssignments().length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {getCompletedAssignments().length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs and Filter */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant={isMobile ? "fullWidth" : "standard"}
            scrollButtons={false}
            sx={{ flex: 1, minWidth: 0 }}
            allowScrollButtonsMobile={false}
          >
            <Tab 
              label={isMobile 
                ? `All (${(assignments || []).filter(a => 
                    assignmentTypeFilter === 'all' || a.assignment_type === assignmentTypeFilter
                  ).length})`
                : `All (${(assignments || []).filter(a => 
                    assignmentTypeFilter === 'all' || a.assignment_type === assignmentTypeFilter
                  ).length})`
              }
              sx={{ 
                minWidth: { xs: 60, sm: 72 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 3 }
              }}
            />
            <Tab 
              label={isMobile 
                ? `Pend (${getPendingAssignments().length})`
                : `Pending (${getPendingAssignments().length})`
              }
              sx={{ 
                minWidth: { xs: 60, sm: 72 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 3 }
              }}
            />
            <Tab 
              label={isMobile 
                ? `In Prog (${getInProgressAssignments().length})`
                : `In Progress (${getInProgressAssignments().length})`
              }
              sx={{ 
                minWidth: { xs: 70, sm: 72 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 3 }
              }}
            />
            <Tab 
              label={isMobile 
                ? `Done (${getCompletedAssignments().length})`
                : `Completed (${getCompletedAssignments().length})`
              }
              sx={{ 
                minWidth: { xs: 60, sm: 72 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 3 }
              }}
            />
          </Tabs>
          
          <FormControl 
            sx={{ 
              minWidth: { xs: 0, sm: 180 }, 
              mx: { xs: 0, sm: 2 },
              display: { xs: 'none', sm: 'flex' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <InputLabel id="assignment-type-filter-label">Filter by Type</InputLabel>
            <Select
              labelId="assignment-type-filter-label"
              id="assignment-type-filter"
              value={assignmentTypeFilter}
              label="Filter by Type"
              onChange={(e) => setAssignmentTypeFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="document">Documents</MenuItem>
              <MenuItem value="training">Training</MenuItem>
              <MenuItem value="checklist">Checklists</MenuItem>
              <MenuItem value="log">Logs</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Assignments List */}
      {filteredAssignments.length === 0 ? (
        <Alert severity="info">
          {selectedTab === 0 
            ? "You don't have any assignments yet." 
            : `No ${['all', 'pending', 'in progress', 'completed'][selectedTab]} assignments.`
          }
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredAssignments.map((assignment) => {
            const daysUntilDue = getDaysUntilDue(assignment.due_date);
            const myCompletion = assignment.assignment_completions?.find(c => c.status !== 'not_started');
            
            return (
              <Grid item xs={12} md={6} lg={4} key={assignment.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: assignment.is_overdue ? '2px solid #f44336' : '1px solid #e0e0e0',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    pb: 1
                  }}>
                    {/* Content container that grows to fill available space */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ flex: 1 }}>
                          {assignment.content_title}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                          <Chip 
                            label={assignment.assignment_type.toUpperCase()}
                            color={getAssignmentTypeColor(assignment.assignment_type) as any}
                            size="small"
                            variant="filled"
                          />
                          <Chip 
                            icon={getStatusIcon(assignment.status, assignment.is_overdue)}
                            label={assignment.assignment_type === 'document' 
                              ? (assignment.document_acknowledgment?.status === 'acknowledged' ? 'ACKNOWLEDGED' : 'PENDING')
                              : assignment.status.replace('_', ' ').toUpperCase()
                            }
                            color={(assignment.assignment_type === 'document'
                              ? (assignment.document_acknowledgment?.status === 'acknowledged' ? 'success' : 'warning')
                              : getStatusColor(assignment.status)) as any}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Revision Information - Shows training version and outdated status */}
                      {assignment.revision_info && assignment.assignment_type === 'training' && (
                        <Box sx={{ mb: 2 }}>
                          <Chip 
                            label={assignment.revision_info.version}
                            color={assignment.revision_info.is_outdated ? 'warning' : 'default'}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              mr: 1,
                              borderColor: assignment.revision_info.is_outdated ? 'warning.main' : 'primary.main',
                              color: assignment.revision_info.is_outdated ? 'warning.main' : 'primary.main'
                            }}
                          />
                          {assignment.revision_info.is_outdated && (
                            <Chip 
                              label="Newer version available"
                              color="warning"
                              size="small"
                              variant="filled"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      )}

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {assignment.description}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Assigned by: {assignment.assigned_by?.first_name} {assignment.assigned_by?.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Due: {formatDate(assignment.due_date)}
                          {daysUntilDue !== null && !isCompliant(assignment) && (
                            <span style={{ 
                              color: daysUntilDue < 0 ? '#f44336' : daysUntilDue <= 3 ? '#ff9800' : '#4caf50',
                              fontWeight: 'bold'
                            }}>
                              {daysUntilDue < 0 
                                ? ` (${Math.abs(daysUntilDue)} days overdue)`
                                : daysUntilDue === 0 
                                  ? ' (Due today)'
                                  : ` (${daysUntilDue} days left)`
                              }
                            </span>
                          )}
                        </Typography>
                      </Box>

                      {myCompletion && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Your Progress: {myCompletion.status.replace('_', ' ')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Score: {myCompletion.score !== null 
                              ? `${myCompletion.score}%` 
                              : 'Not Applicable'}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Button positioned at bottom */}
                    <Button
                      variant="contained"
                      startIcon={<PlayIcon />}
                      onClick={() => {
                        // Check if this is a completed checklist
                        if (assignment.assignment_type === 'checklist' && myCompletion?.status === 'completed') {
                          handleReviewCompletedChecklist(assignment);
                        } else {
                          handleStartAssignment(assignment);
                        }
                      }}
                      fullWidth
                      disabled={false}
                    >
                      {assignment.assignment_type === 'checklist' && myCompletion?.status === 'completed' 
                        ? 'Review Completed Checklist' 
                        : assignment.assignment_type === 'document' && assignment.document_acknowledgment?.status === 'acknowledged'
                          ? 'Review Acknowledged Document'
                          : assignment.assignment_type === 'document'
                            ? 'Acknowledge Document'
                        : assignment.assignment_type === 'log' && myCompletion?.status === 'completed'
                          ? 'Review Submitted Log'
                        : assignment.assignment_type === 'log'
                          ? 'Complete Log'
                        : assignment.status === 'completed' 
                              ? 'Review Training'
                              : myCompletion?.status === 'in_progress'
                                ? 'Continue Assignment'
                                : 'Start Assignment'
                      }
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Completion Dialog */}
      <Dialog open={completionDialog} onClose={handleCloseCompletionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assignment Completed!
        </DialogTitle>
        <DialogContent>
          {completionResults && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Congratulations! You have successfully completed the assignment.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Assignment Summary
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Duration:</strong> {completionResults.duration} minutes
                </Typography>
                {completionResults.testQuestions > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Final Assessment Score:</strong> {completionResults.testCorrectAnswers} of {completionResults.testQuestions} correct
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Percentage:</strong> {completionResults.testScore}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> {completionResults.testPassed ? 'PASSED' : 'FAILED'}
                    </Typography>
                  </>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                <strong>Completed at:</strong> {formatStoredDateTime(completionResults.completedAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompletionDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAssignments; 
